import { Injectable } from "@angular/core";
import { DwdmCircuitViewData } from "../dwdm-circuit.model";
import { LinkInfoCircuit } from "../wizard/steps/4-links/links-step.model";

/**
 * Serviço responsável por avaliar se ocorreram alterações estruturais no circuito.
 */
@Injectable({
    providedIn: "root"
})
export class CircuitStructuralChangeDetectorService {
    hasChange(circuitA: DwdmCircuitViewData, circuitB: DwdmCircuitViewData): boolean {
        const endsChanged = this.hasEndsChange(circuitA, circuitB);

        const transponderChanged = this.hasTransponderChanges(circuitA, circuitB);

        const intermediateChanged = this.intermediateChanged(circuitA, circuitB);

        const linksChanged = this.hasLinksChange(circuitA.linksStepModel.links, circuitB.linksStepModel.links);

        const amplifiersChanged = this.hasAmplifierChanges(circuitA, circuitB);

        const oscChanged = this.hasOscChanges(circuitA, circuitB);

        return endsChanged
            || transponderChanged
            || intermediateChanged
            || linksChanged
            || amplifiersChanged
            || oscChanged;
    }

    private intermediateChanged(circuitA: DwdmCircuitViewData, circuitB: DwdmCircuitViewData) {
        const itemA = this.getOrderedText(circuitA.intermediatesStepModel?.devices.map((device) => device.resourceId.toString()));
        const itemB = this.getOrderedText(circuitB.intermediatesStepModel?.devices.map((device) => device.resourceId.toString()));
        return itemA !== itemB;
    }

    private hasOscChanges(circuitA: DwdmCircuitViewData, circuitB: DwdmCircuitViewData) {
        const useOscA = circuitA.oscManagementStepModel.useOscManagement;
        const useOscB = circuitB.oscManagementStepModel.useOscManagement;
        const useOscChanged = useOscA !== useOscB;

        const oscPortA = this.getOrderedText(
                circuitA.oscManagementStepModel?.oscManagementPorts.map((mngmt) => `${mngmt.oscPort}${mngmt.oscPortNo}`));
        const oscPortB = this.getOrderedText(
                circuitB.oscManagementStepModel?.oscManagementPorts.map((mngmt) => `${mngmt.oscPort}${mngmt.oscPortNo}`));

        const oscMngmtChanged = oscPortA !== oscPortB;

        return useOscChanged || oscMngmtChanged;
    }

    private hasAmplifierChanges(circuitA: DwdmCircuitViewData, circuitB: DwdmCircuitViewData) {
        const amplifiersA = this.getOrderedText(
            circuitA.amplifiersStepModel?.configuredAmplifiers.map((amp) => `${amp.mode}${amp.limit}${amp.value}`));

        const amplifiersB = this.getOrderedText(
            circuitB.amplifiersStepModel?.configuredAmplifiers.map((amp) => `${amp.mode}${amp.limit}${amp.value}`));

        return amplifiersA !== amplifiersB
    }

    private hasTransponderChanges(circuitA: DwdmCircuitViewData, circuitB: DwdmCircuitViewData): boolean {
        const aEndTranspondersPortA = this.getOrderedText(circuitA.transpondersStepModel.aEndPorts.map((port) => port.multirate));
        const aEndTranspondersPortB = this.getOrderedText(circuitB.transpondersStepModel.aEndPorts.map((port) => port.multirate));
        const aEndTransponderMultirateChanged = aEndTranspondersPortA !== aEndTranspondersPortB;

        const zEndTranspondersPortA = this.getOrderedText(circuitA.transpondersStepModel.zEndPorts.map((port) => port.multirate));
        const zEndTranspondersPortB = this.getOrderedText(circuitB.transpondersStepModel.zEndPorts.map((port) => port.multirate));
        const zEndTransponderMultirateChanged = zEndTranspondersPortA !== zEndTranspondersPortB;

        return aEndTransponderMultirateChanged || zEndTransponderMultirateChanged;
    }

    private hasEndsChange(circuitA: DwdmCircuitViewData, circuitB: DwdmCircuitViewData): boolean {
        const aEndChanged = circuitA.endsStepModel.aEndDevice.id !== circuitB.endsStepModel.aEndDevice.id;
        const zEndChanged = circuitA.endsStepModel.zEndDevice.id !== circuitB.endsStepModel.zEndDevice.id;
        const aEndHasMuxChanged = circuitA.endsStepModel.aEndHasMux !== circuitB.endsStepModel.aEndHasMux;
        const zEndHasMuxChanged = circuitA.endsStepModel.zEndHasMux !== circuitB.endsStepModel.zEndHasMux;
        const aEndMuxChanged = circuitA.endsStepModel?.aEndMux?.resourceId !== circuitB.endsStepModel?.aEndMux?.resourceId;
        const zEndMuxChanged = circuitA.endsStepModel?.zEndMux?.resourceId !== circuitB.endsStepModel?.zEndMux?.resourceId;

        const aEndPortsA = this.getOrderedText(circuitA.endsStepModel.aEndPorts.map((port) => port.portName));
        const aEndPortsB = this.getOrderedText(circuitB.endsStepModel.aEndPorts.map((port) => port.portName));
        const aEndPortsChanged = aEndPortsA !== aEndPortsB;

        const zEndPortsA = this.getOrderedText(circuitA.endsStepModel.aEndPorts.map((port) => port.portName));
        const zEndPortsB = this.getOrderedText(circuitB.endsStepModel.zEndPorts.map((port) => port.portName));
        const zEndPortsChanged = zEndPortsA !== zEndPortsB;

        return aEndChanged
            || zEndChanged
            || aEndHasMuxChanged
            || zEndHasMuxChanged
            || aEndMuxChanged
            || zEndMuxChanged
            || aEndPortsChanged
            || zEndPortsChanged;
    }

    private hasLinksChange(linksA: Array<LinkInfoCircuit>, linksB: Array<LinkInfoCircuit>): boolean {
        const linkAIdentifier = this.getOrderedText(linksA.map((link) => `${link.id}`));
        const linkBIdentifier = this.getOrderedText(linksB.map((link) => `${link.id}`));

        return linkAIdentifier !== linkBIdentifier;
    }

    /**
     * Ordena os itens do array e junta todos em uma string
     *
     * @param itens Array de strings a ser ordenado e convertido em string
     * @returns string resultante da junção de todos os elementos do array.
     */
    private getOrderedText(itens: Array<string>): string {
        return [...itens].sort((a, b) => a.localeCompare(b)).join();
    }
}
